import { convertToTitleCase } from "./helpers";

/**
 * Format communication channel name for display
 * @param {string} channel - Channel name (email, sms, push)
 * @returns {string} Formatted channel name
 */
export const formatChannelName = (channel) => {
  if (!channel) return "Unknown";
  return convertToTitleCase(channel);
};

/**
 * Get channel icon class
 * @param {string} channel - Channel name (email, sms, push)
 * @returns {string} Font Awesome icon class
 */
export const getChannelIcon = (channel) => {
  const icons = {
    email: "fas fa-envelope",
    sms: "fas fa-comment-alt",
    push: "fas fa-bell",
  };
  return icons[channel?.toLowerCase()] || "fas fa-question-circle";
};

/**
 * Get status badge color based on status
 * @param {string} status - Status (pending, sent, failed, delivered, bounced)
 * @returns {string} Hex color code
 */
export const getStatusBadgeColor = (status) => {
  const statusLower = (status || "").toLowerCase();
  switch (statusLower) {
    case "pending":
    case "drafted":
    case "scheduled":
      return "#FF9500"; // Orange
    case "sent":
    case "delivered":
      return "#19BC31"; // Green
    case "partial":
      return "#FF9500"; // Orange
    case "failed":
    case "bounced":
      return "#FF4343"; // Red
    default:
      return "#A1A0A0"; // Grey
  }
};

/**
 * Get status badge text
 * @param {string} status - Status
 * @returns {string} Formatted status text
 */
export const getStatusBadgeText = (status) => {
  return convertToTitleCase(status || "Unknown");
};

/**
 * Format recipient display name
 * @param {object} recipient - Recipient object with name, first_name, last_name
 * @returns {string} Formatted recipient name
 */
export const formatRecipientName = (recipient) => {
  if (!recipient) return "Unknown";
  
  if (recipient.name) {
    return recipient.name;
  }
  
  const firstName = recipient.first_name || "";
  const lastName = recipient.last_name || "";
  const fullName = `${firstName} ${lastName}`.trim();
  
  return fullName || "Unknown";
};

/**
 * Truncate message preview
 * @param {string} message - Full message text
 * @param {number} maxLength - Maximum length (default: 100)
 * @returns {string} Truncated message with ellipsis
 */
export const truncateMessage = (message, maxLength = 100) => {
  if (!message) return "No message";
  if (message.length <= maxLength) return message;
  return message.substring(0, maxLength) + "...";
};

/**
 * Get channel color for UI elements
 * @param {string} channel - Channel name
 * @returns {string} Hex color code
 */
export const getChannelColor = (channel) => {
  const colors = {
    email: "var(--color-secondary)", // Teal
    sms: "var(--color-primary)", // Primary/Burgundy
    push: "#FF9500", // Orange
  };
  return colors[channel?.toLowerCase()] || "#A1A0A0";
};
